import React, { useState } from 'react';
import { useVerifyUserCode, useVerifyUserEmail } from '../../api/users';
import { useLoginModalContext } from '../../context/LoginModalContext';
import './LoginModal.css';
import { useUserContext } from '../../context/UserContext';
import { X, Sun } from 'lucide-react';

interface LoginModalVerificationProps {
  onClose: () => void;
}

const LoginModalVerification: React.FC<LoginModalVerificationProps> = ({ 
  onClose
}) => {
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const verifyUserMutation = useVerifyUserCode();
  const { email, setCurrentStep } = useLoginModalContext();
  const { login } = useUserContext();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (email.trim()) {
      try {
        const result = await verifyUserMutation.mutateAsync({ 
          email: email.trim(), 
          code: code.trim()
        });
         console.log('User verified successfully:', result);
         login(result.user);
         setCurrentStep('new_password');
      } catch (error: any) {
        console.error('Login failed:', error);
        
        if (error?.response?.status === 404) {
          setErrorMessage('No user found with this email. Please register.');
        } else if (error?.message) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('Login failed. Please try again.');
        }
      }
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button 
            className="modal-close-btn" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>

          <div className="sun-icon">
            <Sun size={48} color="var(--primary-color)" />
          </div>

          <h1 className="modal-title">Verify Your Account!</h1>
          
          <p className="modal-description">
            Enter the 6 digit verification code sent to your email to log in.
          </p>
          
          {errorMessage && (
            <div className="message message--error">
              {errorMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="verificationCode">Verification Code</label>
              <input
                type="text"
                id="verificationCode"
                className="form-input"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder="Enter 6-digit code"
                required
                maxLength={6}
                disabled={verifyUserMutation.isPending}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn--primary btn--login"
              disabled={verifyUserMutation.isPending}
            >
              {verifyUserMutation.isPending ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default LoginModalVerification;
